// Starter blueprints (ENG-1022) — what a character's graph is when NOBODY has
// authored one yet.
//
// A blueprint is only ever created by a person saving one: nothing on the server
// mints a row (characters.go and services/texavatars_assets have zero blueprint
// references), and the client's writeEmptyBlueprintsForCharacter writes
// localStorage ONLY. So a character that was uploaded rather than hand-authored
// has no graph at all, and something has to stand in.
//
// What used to stand in for a splat character was `blueprints/isaac.json` — one
// CHARACTER's asset doing a SYSTEM's job. Two things were wrong with it, and they
// are different problems:
//
//   1. It has no `plugin.ogs.renderer` node and never has, so resolveRenderPhase
//      settled at 'three' ("no OGS work") and the inference pipeline never
//      started. myraclothes rendered nothing, and the only clue was a 10s
//      block-detector message. The docstring on writeEmptyBlueprintsForCharacter
//      still claimed splat characters "pull those from isaac.json"; they could not.
//   2. Deleting that file would have errored EVERY gaussian character at once
//      ("loadBlueprint /blueprints/isaac.json: 404"), naming a character that had
//      nothing to do with the one that broke.
//
// So a default is named after the KIND of character it serves, never after a
// character. Adding a kind is a row in STARTER_TEMPLATES.
//
// The starter is DERIVED PER LOAD and never written to the server. A persisted
// default freezes the template at mint time — every later improvement becomes a
// migration over existing rows — and it destroys the distinction between "the
// author chose exactly this" and "nobody has chosen anything yet", which is the
// one bit of information needed to know whether regenerating is safe. The first
// real save creates version 1.
//
// Pure — no fetch, no React, no module singletons. The one impure step (reading
// the bundle's scene.json) is the caller's, so every rule below is unit-testable.

import { branchPortNames } from '../ogs/graph/branchPorts.ts'

// Template per character KIND. Never a character's own file.
//
// `rig` deliberately still points at conversational.json: it is generically
// named, so it is not the landmine isaac.json was, and it is referenced by an
// order of magnitude more e2e specs. What matters here is the INDIRECTION —
// callers ask for a kind, so moving that content to its own starter file later
// is a one-line edit here and no change at any call site.
export const STARTER_TEMPLATES = {
  rig: 'blueprints/conversational.json',
  gaussian: 'blueprints/templates/starter-gaussian.json',
}

/** Which starter a character gets. `splat` is isSplatCharacter(character) — passed
 *  in rather than imported so this module stays free of the character/API layer. */
export function resolveStarterKind({ splat = false } = {}) {
  return splat ? 'gaussian' : 'rig'
}

export function starterTemplateUrl(kind) {
  const url = STARTER_TEMPLATES[kind]
  if (!url) throw new Error(`starterTemplateUrl: no template for kind "${kind}"`)
  return url
}

// The primary region. It sits on the renderer's REQUIRED geom_uv/color_uv pins,
// so it is the one region whose absence stalls the whole chain.
const PRIMARY_REGION = 'head'

/**
 * Region names for a bundle, in render order, head first.
 *
 * `scene` is the bundle's parsed scene.json. A multi_region bundle names its
 * branches there; a single-region bundle ships no scene.json at all (its manifest
 * is synthesized by sceneManifest.singleRegionScene as one fused 'head' branch),
 * so a null/blank scene resolves to exactly ['head'] rather than to nothing.
 * Returning [] here would produce a renderer with nothing on its required pins —
 * the very stall this module exists to prevent.
 */
export function regionsFromScene(scene) {
  const branches = scene?.branches
  const names = branches && typeof branches === 'object' ? Object.keys(branches) : []
  if (!names.length) return [PRIMARY_REGION]
  // head first; the rest keep the bundle's own order so the graph reads like the
  // manifest it came from.
  return [...names.filter((n) => n === PRIMARY_REGION), ...names.filter((n) => n !== PRIMARY_REGION)]
}

/**
 * The regions of a character's bundle, from its /ogs listing.
 *
 * `fetchJson` is injected (the caller supplies the AAM-authenticated reader), so
 * this module still names no endpoint and stays testable without a network. The
 * URL comes back verbatim from the listing row — the client never derives one.
 *
 * Deliberately NOT resolveBundleManifest: that needs a live VFS map, seeds it for
 * the bulk download, and throws for a bundle it cannot fully resolve. Blocking
 * the blueprint on the whole bundle resolution would couple graph hydration to
 * the inference load. All that is needed here is the branch names.
 *
 * Any failure resolves to the primary region alone rather than throwing: a
 * starter with a head branch renders the character's face, while no starter at
 * all renders nothing. The caller reports the failure — this never fakes a
 * region that the bundle did not name.
 */
export async function fetchStarterRegions({ rows, fetchJson }) {
  const row = (rows ?? []).find((r) => (r?.filename ?? r?.name) === 'scene.json')
  const url = row ? (row.fileUrl ?? row.file_url) : null
  if (!url || typeof fetchJson !== 'function') return { regions: [PRIMARY_REGION], error: null }
  try {
    return { regions: regionsFromScene(await fetchJson(url)), error: null }
  } catch (e) {
    return { regions: [PRIMARY_REGION], error: e?.message ?? String(e) }
  }
}

/**
 * Build the branch cluster for a set of regions.
 *
 * One `plugin.ogs.branch` per region, its rig driven by the face graph's final
 * pose, its UV outputs wired to the renderer pins branchPortNames dictates —
 * head to the primary pair, every other region to its own. That naming is not a
 * convention this module may reinterpret: HeadTeethEngine._collect keys its
 * inbound map by port name, so two regions sharing a pair silently lifts one
 * region's decode onto another's mesh.
 *
 * `declaredPins` (the renderer manifest's dataIn ids) is OPTIONAL and is not
 * supplied at runtime. branchPortNames IS the contract, and that the manifest
 * agrees with it is enforced by tests (this module's, plus branchPorts.test.mjs)
 * — a build-time check with no race. Reading the manifest here instead would
 * mean asking the plugin registry, which fills in asynchronously: a blueprint
 * that hydrates first would see no declared pins and skip every region, turning
 * a load-order detail into a blank avatar.
 *
 * When a caller DOES supply pins, a region with no pair is SKIPPED and returned
 * in `skipped` to be reported — never dropped silently, and never allowed to
 * take the rest of the avatar down with it.
 */
export function buildBranchCluster(regions, { declaredPins, rendererId = 'ogs_renderer', finalId = 'final' } = {}) {
  const declared = declaredPins ? new Set(declaredPins) : null
  const nodes = []
  const edges = []
  const skipped = []

  regions.forEach((region, i) => {
    const [geomPin, colorPin] = branchPortNames(region, i)
    if (declared && !(declared.has(geomPin) && declared.has(colorPin))) {
      skipped.push({ region, pins: [geomPin, colorPin] })
      return
    }
    const id = `ogs_branch_${region}`
    nodes.push({
      id,
      type: 'plugin.ogs.branch',
      position: { x: 2250, y: 330 + nodes.length * 230 },
      data: { region },
    })
    edges.push(
      { id: `e_rig_${region}`, source: finalId, target: id, sourceHandle: 'pose', targetHandle: 'rig' },
      { id: `e_${region}_geom`, source: id, target: rendererId, sourceHandle: 'geom_uv', targetHandle: geomPin },
      { id: `e_${region}_color`, source: id, target: rendererId, sourceHandle: 'color_uv', targetHandle: colorPin },
    )
  })

  return { nodes, edges, skipped }
}

/**
 * Instantiate a gaussian starter for a bundle's regions.
 *
 * Returns a NEW blueprint object — the template is a fetched document that may be
 * cached and reused for the next character, so mutating it in place would leak
 * one character's regions into another's graph.
 *
 * A template with no renderer node is returned untouched: there is nothing to
 * wire branches to, and inventing a renderer here would hide a broken template
 * behind a graph that looks fine.
 *
 * A document that ALREADY declares branches is also returned untouched, so this
 * is idempotent and safe to run over an authored graph: appending a second set
 * would put two branch nodes on one region, and the engine keys its inbound map
 * by port name, so the duplicate pair would silently shadow the first.
 */
export function applyStarterRegions(data, regions, { declaredPins } = {}) {
  const face = data?.face
  if (!face || !Array.isArray(face.nodes)) return { data, skipped: [] }
  const renderer = face.nodes.find((n) => n.type === 'plugin.ogs.renderer')
  if (!renderer) return { data, skipped: [] }
  if (face.nodes.some((n) => n.type === 'plugin.ogs.branch')) return { data, skipped: [] }

  const { nodes, edges, skipped } = buildBranchCluster(regions, {
    declaredPins,
    rendererId: renderer.id,
    finalId: face.nodes.find((n) => n.type === 'finalPose')?.id ?? 'final',
  })

  return {
    data: {
      ...data,
      face: {
        ...face,
        nodes: [...face.nodes, ...nodes],
        edges: [...(face.edges ?? []), ...edges],
      },
    },
    skipped,
  }
}
